/**
 * **Auswirkungen eines Abonnements** — das Dashboard im Popup einer Abonnement-Zeile.
 *
 * Was ein Pflichtenabonnement nach sich zieht — vor dem Aktivieren als Vorausschau, danach tatsächlich: offene und überfällige Checklisten,
 * was im nächsten Quartal, in einem und in zwei Jahren ansteht — je Rolle — und die geschätzten Kosten.
 * Die Zahlen kommen aus `GET /legal/dashboard` (tri-server `subscriptionDashboard`): offene
 * Checklisten plus die Fälligkeiten, die die Timer bis dahin erzeugen; Kosten aus der
 * Kostenparametrierung, einmalig plus anteilig jährlich.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import ReactApexChart from "react-apexcharts";
import { AlertTriangle } from "lucide-react";
import Section from "../../components/Section";
import Metric from "../../components/Metric";
import { useModalContext } from "../../components/ModalProvider";
import { useCustomerTheme } from "../../Theme";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { qualificationRoleName, regulatedObjectName } from "../../administration/operator-responsibility/legalDisplay";
import MergedChecklistPreviewDialog, { MergedAppointmentRef } from "./MergedChecklistPreviewDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ScrollToBottomButton from "../../components/ScrollToBottomButton";
import { Eye } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const RISK_BADGE: Record<string, string> = {
  hoch: "bg-red-600 text-white hover:bg-red-600",
  mittel: "bg-amber-500 text-white hover:bg-amber-500",
  gering: "bg-emerald-600 text-white hover:bg-emerald-600",
  ohne: "bg-muted text-muted-foreground hover:bg-muted",
};
const DAMAGE_ORDER = ["person", "umwelt", "sach", "recht"];

/**
 * A tab's own scroll area with the chats' jump-to-end button: most readers do not scroll a long page to find
 * what is below (16.10.2026: the merged appointments sat under three charts and two tables), so the dashboard
 * is split into tabs, and a tab that is longer than the view says so with the arrow.
 */
function ScrollPane({ children, watch }: { children: React.ReactNode; watch?: unknown }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const scroller = useCallback(() => ref.current, []);
  return (
    <div className="relative">
      <div ref={ref} className="flex max-h-[62vh] min-w-0 flex-col gap-4 overflow-y-auto pr-1">
        {children}
      </div>
      <ScrollToBottomButton scroller={scroller} watch={watch} />
    </div>
  );
}

interface Properties {
  sites: any[];
  refreshSignal?: number;
  /** The subscription shown. Without one, all subscriptions with a selector. */
  bindingId?: string;
}

/** While the server computes (a large site takes ~30 s), ask again every few seconds. */
const POLL_MS = 3000;

/**
 * An axis label in lines of at most `width` characters (ApexCharts takes an array as a multi-line label).
 * Long role names side by side overlapped, and ApexCharts silently dropped every other one — „Befähigte
 * Person“ had no label under its bars.
 */
export const wrapLabel = (text: string, width = 16): string[] => {
  const lines: string[] = [];
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const last = lines[lines.length - 1];
    if (last && (last + " " + word).length <= width) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  return lines;
};

const ALL = "all";
const HORIZONS = ["quarter", "year1", "year2"] as const;

export default function SubscriptionDashboardPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const { theme } = useCustomerTheme();
  const [authentication] = useAtom(authenticationAtom) as any;
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  const fixed = !!properties.bindingId;
  const [selected, setSelected] = useState<string>(properties.bindingId ?? ALL);
  const [computingSince, setComputingSince] = useState<string | null>(null);
  useEffect(() => {
    if (properties.bindingId) setSelected(properties.bindingId);
  }, [properties.bindingId]);
  const [data, setData] = useState<any>(null);
  const [mergedPreview, setMergedPreview] = useState<MergedAppointmentRef | null>(null);
  const [tab, setTab] = useState("overview");
  const [failed, setFailed] = useState(false);
  // The selector lists every activated subscription, also while one is shown.
  const [allBindings, setAllBindings] = useState<any[]>([]);

  useEffect(() => {
    if (!authentication?.customer) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setData(null);
    setFailed(false);
    setComputingSince(null);
    const fetchOnce = async () => {
      try {
        const result = await legalAPI.getDashboard(selected === ALL ? undefined : selected);
        if (cancelled) return;
        if (result?.status === "computing") {
          setComputingSince(result.since ?? null);
          timer = setTimeout(fetchOnce, POLL_MS);
          return;
        }
        setComputingSince(null);
        setData(result);
      } catch (error) {
        if (!cancelled) {
          setFailed(true);
          pushError(error);
        }
      }
    };
    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, selected, properties.refreshSignal]);

  // The selector offers every subscription — from the plain list, the dashboard itself may take long.
  useEffect(() => {
    if (!authentication?.customer) return;
    legalAPI
      .getBindings()
      .then((list: any[]) =>
        setAllBindings(
          (list || [])
            .filter((binding: any) => binding.status !== "ended")
            .map((binding: any) => ({ ...binding, _id: String(binding._id), projected: binding.activation !== "activated" }))
        )
      )
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, properties.refreshSignal]);

  const colors = useMemo(
    () => [theme.secondary, theme.tertiary, theme.primary, theme.secondaryLight, theme.tertiaryLight, theme.primaryLight].filter(Boolean),
    [theme]
  );

  const siteName = (siteId: string | null) => {
    if (!siteId) return "—";
    const site = (properties.sites || []).find((s: any) => String(s._id) === String(siteId));
    return site ? site.name || `${site.street} ${site.streetNumber}` : siteId;
  };
  const scopeLabel = (binding: any) =>
    `${binding.scope?.anchor?.type === "Site" ? siteName(binding.scope.anchor.siteId) : t("operatorObligationsPanel.portfolio")} · ${t(
      `operatorObligationsPanel.conformance.${binding.conformanceClass}`
    )}`;

  const currency = data?.currency || "EUR";
  const money = (value: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency, maximumFractionDigits: 0 }).format(value || 0);
  const count = (value: number) => new Intl.NumberFormat("de-DE").format(value || 0);
  const roleName = (role: string) => qualificationRoleName(t, role);
  const horizonLabel = (key: string) => t(`operatorObligationsPanel.dashboard.horizon.${key}`);

  if (failed) {
    return (
      <Alert variant="destructive">
        <AlertTriangle aria-hidden className="h-4 w-4" />
        <AlertDescription>{t("operatorObligationsPanel.dashboard.loadFailed")}</AlertDescription>
      </Alert>
    );
  }
  if (!data) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4" data-testid="dashboard-loading">
        <p className="text-sm text-muted-foreground">
          {computingSince
            ? t("operatorObligationsPanel.dashboard.computing", {
                seconds: Math.max(1, Math.round((Date.now() - new Date(computingSince).getTime()) / 1000)),
              })
            : t("operatorObligationsPanel.dashboard.loading")}
        </p>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const horizon = (key: string) => data.horizons.find((h: any) => h.key === key) ?? { checklists: { total: 0, byRole: {} }, cost: { total: 0, byRole: {} } };
  const roles: string[] = data.roles ?? [];

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4" data-testid="subscription-dashboard">
      <div className="flex flex-wrap items-center gap-3">
        {fixed ? null : (
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-full sm:w-[420px]" aria-label={t("operatorObligationsPanel.dashboard.subscription")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("operatorObligationsPanel.dashboard.allActivated")}</SelectItem>
            {allBindings.map((binding: any) => (
              <SelectItem key={binding._id} value={binding._id}>
                {scopeLabel(binding)}
                {binding.projected ? ` · ${t("operatorObligationsPanel.dashboard.projectedShort")}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        <span className="text-xs text-muted-foreground">
          {t("operatorObligationsPanel.dashboard.asOf", { date: new Date(data.generatedAt).toLocaleString("de-DE") })}
        </span>
      </div>

      {data.projected && data.bindings.length ? (
        <Alert>
          <AlertDescription>{t("operatorObligationsPanel.dashboard.projected")}</AlertDescription>
        </Alert>
      ) : null}

      {!data.bindings.length ? (
        <Alert>
          <AlertDescription>{t("operatorObligationsPanel.dashboard.noneActivated")}</AlertDescription>
        </Alert>
      ) : (
        <>
        <Tabs value={tab} onValueChange={setTab} className="flex min-w-0 flex-col gap-3">
          <TabsList className="h-auto flex-wrap justify-start">
            <TabsTrigger value="overview">{t("operatorObligationsPanel.dashboard.tabs.overview")}</TabsTrigger>
            <TabsTrigger value="priorities" data-testid="dashboard-tab-priorities">
              {t("operatorObligationsPanel.dashboard.tabs.priorities")}
              {data.priorities?.byClass?.hoch ? (
                <span className="ml-1.5 rounded-full bg-red-600/15 px-1.5 text-xs tabular-nums text-red-700 dark:text-red-300">
                  {data.priorities.byClass.hoch.toLocaleString("de-DE")}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="checklists">{t("operatorObligationsPanel.dashboard.tabs.checklists")}</TabsTrigger>
            <TabsTrigger value="cost">{t("operatorObligationsPanel.dashboard.tabs.cost")}</TabsTrigger>
            <TabsTrigger value="merged" data-testid="dashboard-tab-merged">
              {t("operatorObligationsPanel.dashboard.tabs.merged")}
              {data.appointments?.merged?.length ? (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs tabular-nums">{data.appointments.merged.length}</span>
              ) : null}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-0">
            <ScrollPane watch={tab}>
                <div className="grid min-w-0 grid-cols-2 gap-3 md:grid-cols-4">
                  <Metric title={t("operatorObligationsPanel.dashboard.openNow")} value={count(data.open.total)} color={theme.primary} size="large" />
                  <Metric title={t("operatorObligationsPanel.dashboard.overdue")} value={count(data.open.overdue)} color={theme.tertiary} size="large" />
                  {HORIZONS.map(key => (
                    <Metric
                      key={`c-${key}`}
                      title={t("operatorObligationsPanel.dashboard.checklistsIn", { horizon: horizonLabel(key) })}
                      value={count(horizon(key).checklists.total)}
                      color={theme.secondary}
                      size="large"
                    />
                  ))}
                  {HORIZONS.map(key => (
                    <Metric
                      key={`k-${key}`}
                      title={t("operatorObligationsPanel.dashboard.costIn", { horizon: horizonLabel(key) })}
                      value={money(horizon(key).cost.total)}
                      color={theme.tertiary}
                      size="large"
                    />
                  ))}
                </div>
                {data.priorities ? (
                  <button
                    type="button"
                    className="mt-3 grid w-full min-w-0 grid-cols-2 gap-3 text-left md:grid-cols-4"
                    onClick={() => setTab("priorities")}
                    title={t("operatorObligationsPanel.priorities.title")}
                    data-testid="dashboard-overview-risk"
                  >
                    <Metric title={t("operatorObligationsPanel.priorities.metricHigh")} value={count(data.priorities.byClass.hoch ?? 0)} color="#dc2626" size="large" />
                    <Metric title={t("operatorObligationsPanel.priorities.metricHighOverdue")} value={count(data.priorities.overdueByClass?.hoch ?? 0)} color="#dc2626" size="large" />
                    <Metric title={t("operatorObligationsPanel.priorities.metricMedium")} value={count(data.priorities.byClass.mittel ?? 0)} color="#f59e0b" size="large" />
                    <Metric
                      title={t("operatorObligationsPanel.priorities.metricDefects")}
                      value={count((data.priorities.openDefects?.critical ?? 0) + (data.priorities.openDefects?.major ?? 0) + (data.priorities.openDefects?.minor ?? 0))}
                      color={theme.tertiary}
                      size="large"
                    />
                  </button>
                ) : null}

                {data.flags?.presetRoles?.length ? (
                  <Alert data-testid="dashboard-preset-roles">
                    <AlertDescription>
                      {t("operatorObligationsPanel.dashboard.presetRoles", { roles: data.flags.presetRoles.map(roleName).join(", ") })}
                    </AlertDescription>
                  </Alert>
                ) : null}
                {data.flags?.unconfiguredRoles?.length || data.flags?.timersWithoutInterval ? (
                  <Alert>
                    <AlertTriangle aria-hidden className="h-4 w-4" />
                    <AlertDescription className="flex flex-col gap-1">
                      {data.flags.unconfiguredRoles?.length ? (
                        <span>
                          {t("operatorObligationsPanel.dashboard.unconfiguredRoles", {
                            roles: data.flags.unconfiguredRoles.map(roleName).join(", "),
                          })}
                        </span>
                      ) : null}
                      {data.flags.timersWithoutInterval ? (
                        <span>{t("operatorObligationsPanel.dashboard.timersWithoutInterval", { count: data.flags.timersWithoutInterval })}</span>
                      ) : null}
                    </AlertDescription>
                  </Alert>
                ) : null}
                <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.dashboard.method")}</p>
            </ScrollPane>
          </TabsContent>
          <TabsContent value="priorities" className="mt-0">
            <ScrollPane watch={tab}>
              {data.priorities ? (
                <Section title={t("operatorObligationsPanel.priorities.title")}>
                  <div className="mb-3 flex flex-col gap-1.5 text-sm" data-testid="dashboard-priorities-summary">
                    <p>
                      {t("operatorObligationsPanel.priorities.summary", {
                        high: (data.priorities.byClass.hoch ?? 0).toLocaleString("de-DE"),
                        medium: (data.priorities.byClass.mittel ?? 0).toLocaleString("de-DE"),
                        low: (data.priorities.byClass.gering ?? 0).toLocaleString("de-DE"),
                        none: (data.priorities.byClass.ohne ?? 0).toLocaleString("de-DE"),
                      })}
                      {data.priorities.overdueByClass?.hoch
                        ? ` (${t("operatorObligationsPanel.priorities.overdue", { count: data.priorities.overdueByClass.hoch })})`
                        : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.priorities.explain")}</p>
                    {data.priorities.ewPlaceholderShare > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {t("operatorObligationsPanel.priorities.placeholder", { percent: Math.round(data.priorities.ewPlaceholderShare * 100) })}
                      </p>
                    ) : null}
                    {data.priorities.highWithOperatorIntervalOnly ? (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          {t("operatorObligationsPanel.priorities.operatorOnly", { count: data.priorities.highWithOperatorIntervalOnly })}
                        </AlertDescription>
                      </Alert>
                    ) : null}
                    {Object.values(data.priorities.openDefects ?? {}).some(Boolean) ? (
                      <p>{String(t("operatorObligationsPanel.priorities.defects", { ...data.priorities.openDefects }))}</p>
                    ) : null}
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("operatorObligationsPanel.priorities.risk")}</TableHead>
                          <TableHead>{t("operatorObligationsPanel.priorities.asset")}</TableHead>
                          <TableHead>{t("operatorObligationsPanel.priorities.object")}</TableHead>
                          <TableHead className="text-right">{t("operatorObligationsPanel.priorities.interval")}</TableHead>
                          <TableHead>{t("operatorObligationsPanel.priorities.due")}</TableHead>
                          <TableHead>{t("operatorObligationsPanel.priorities.severity")}</TableHead>
                          <TableHead>{t("operatorObligationsPanel.priorities.ew")}</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(data.priorities.top ?? []).map((row: any) => {
                          const risk = row.risk ?? {};
                          const riskClass = risk.riskClass ?? "ohne";
                          return (
                            <TableRow key={`${row.bindingId}|${row.assetId}|${row.intervalMonths}|${row.obligationKeys.join(",")}`}>
                              <TableCell>
                                <Badge className={RISK_BADGE[riskClass] ?? RISK_BADGE.ohne}>
                                  {t(`operatorObligationsPanel.priorities.class.${riskClass}`)}
                                </Badge>
                              </TableCell>
                              <TableCell className="break-words">{row.assetName ?? row.assetId}</TableCell>
                              <TableCell className="break-words">{regulatedObjectName(t, row.regulatedObjectCode)}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {/* null: a due date without a resolvable interval — older servers sent 0 for it */}
                                {row.intervalMonths > 0
                                  ? t("operatorObligationsPanel.merged.everyMonths", { count: row.intervalMonths })
                                  : t("operatorObligationsPanel.priorities.intervalOpen")}
                              </TableCell>
                              <TableCell className={`tabular-nums ${row.overdue ? "font-semibold text-red-700 dark:text-red-300" : ""}`}>
                                {row.nextDueAt ? new Date(row.nextDueAt).toLocaleDateString("de-DE") : "—"}
                              </TableCell>
                              <TableCell className="min-w-[180px] text-xs">
                                {DAMAGE_ORDER.filter(d => risk.byDamage?.[d]).map(d => (
                                  <div key={d} className={d === risk.leadingDamage ? "font-medium" : "text-muted-foreground"}>
                                    {t(`operatorObligationsPanel.priorities.damage.${d}`)}: si {risk.byDamage[d].si} · R {risk.byDamage[d].r}
                                    {risk.byDamage[d].barrier ? ` · ${t("operatorObligationsPanel.priorities.barrier")}` : ""}
                                  </div>
                                ))}
                                {risk.hazardSource === "anlage" && risk.riskClass !== "ohne" ? (
                                  <div className="italic text-muted-foreground">{t("operatorObligationsPanel.priorities.fromAsset")}</div>
                                ) : null}
                              </TableCell>
                              <TableCell className="text-xs tabular-nums">
                                {risk.ew != null ? risk.ew.toLocaleString("de-DE") : "—"}
                                <div className="text-muted-foreground">
                                  {risk.ewInputs?.length ? risk.ewInputs.join(", ") : t("operatorObligationsPanel.priorities.ewBase")}
                                  {risk.assumed?.includes("usageClass") ? `; ${t("operatorObligationsPanel.priorities.assumedUsage")}` : ""}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={t("operatorObligationsPanel.merged.showChecklist")}
                                  title={t("operatorObligationsPanel.merged.showChecklist")}
                                  onClick={() =>
                                    setMergedPreview({
                                      obligationKeys: row.obligationKeys,
                                      sampleAssetId: row.assetId,
                                      sampleAssetName: row.assetName,
                                      intervalMonths: row.intervalMonths,
                                    })
                                  }
                                >
                                  <Eye aria-hidden className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Section>
              ) : null}
            </ScrollPane>
          </TabsContent>
          <TabsContent value="checklists" className="mt-0">
            <ScrollPane watch={tab}>
                <Section title={t("operatorObligationsPanel.dashboard.perQuarter")}>
                  <div className="min-w-0" data-testid="dashboard-chart-quarters">
                    <ReactApexChart
                      type="bar"
                      height={320}
                      options={{
                        chart: { type: "bar", stacked: true, toolbar: { show: false } },
                        xaxis: { categories: data.timeline.map((q: any) => q.label) },
                        yaxis: { title: { text: t("operatorObligationsPanel.dashboard.checklists") } },
                        colors,
                        dataLabels: { enabled: false },
                        legend: { position: "bottom" },
                      } as any}
                      series={roles.map(role => ({
                        name: roleName(role),
                        data: data.timeline.map((q: any) => q.checklists.byRole?.[role] ?? 0),
                      }))}
                    />
                  </div>
                </Section>

            </ScrollPane>
          </TabsContent>
          <TabsContent value="cost" className="mt-0">
            <ScrollPane watch={tab}>
                <Section title={t("operatorObligationsPanel.dashboard.costPerRole")}>
                  <div className="min-w-0" data-testid="dashboard-chart-cost">
                    <ReactApexChart
                      type="bar"
                      height={320}
                      options={{
                        chart: { type: "bar", toolbar: { show: false } },
                        xaxis: {
                          categories: roles.map(role => wrapLabel(roleName(role))),
                          labels: { hideOverlappingLabels: false, trim: false, rotate: 0 },
                        },
                        yaxis: { labels: { formatter: (value: number) => money(value) } },
                        tooltip: { y: { formatter: (value: number) => money(value) } },
                        colors,
                        dataLabels: { enabled: false },
                        legend: { position: "bottom" },
                      } as any}
                      series={HORIZONS.map(key => ({
                        name: horizonLabel(key),
                        data: roles.map(role => horizon(key).cost.byRole?.[role] ?? 0),
                      }))}
                    />
                  </div>
                </Section>

                <Section title={t("operatorObligationsPanel.dashboard.byRole")}>
                  <div className="min-w-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("operatorResponsibilityPanel.roleLabel")}</TableHead>
                          <TableHead className="text-right">{t("operatorObligationsPanel.dashboard.openNow")}</TableHead>
                          {HORIZONS.map(key => (
                            <TableHead key={`h-c-${key}`} className="text-right">
                              {t("operatorObligationsPanel.dashboard.checklistsShort", { horizon: horizonLabel(key) })}
                            </TableHead>
                          ))}
                          {HORIZONS.map(key => (
                            <TableHead key={`h-k-${key}`} className="text-right">
                              {t("operatorObligationsPanel.dashboard.costShort", { horizon: horizonLabel(key) })}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {roles.map(role => (
                          <TableRow key={role}>
                            <TableCell>{roleName(role)}</TableCell>
                            <TableCell className="text-right tabular-nums">{count(data.open.byRole?.[role] ?? 0)}</TableCell>
                            {HORIZONS.map(key => (
                              <TableCell key={`c-${key}`} className="text-right tabular-nums">
                                {count(horizon(key).checklists.byRole?.[role] ?? 0)}
                              </TableCell>
                            ))}
                            {HORIZONS.map(key => (
                              <TableCell key={`k-${key}`} className="text-right tabular-nums">
                                {money(horizon(key).cost.byRole?.[role] ?? 0)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Section>

            </ScrollPane>
          </TabsContent>
          <TabsContent value="merged" className="mt-0">
            <ScrollPane watch={tab}>
                {data.appointments ? (
                  <Section title={t("operatorObligationsPanel.merged.title")}>
                    <p className="mb-2 text-sm" data-testid="dashboard-merged-summary">
                      {t("operatorObligationsPanel.merged.summary", {
                        duties: data.appointments.duties.toLocaleString("de-DE"),
                        appointments: data.appointments.appointments.toLocaleString("de-DE"),
                        separate: data.appointments.checklistsPerYearSeparate.toLocaleString("de-DE"),
                        merged: data.appointments.checklistsPerYearMerged.toLocaleString("de-DE"),
                      })}
                    </p>
                    {data.appointments.merged?.length ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("operatorObligationsPanel.merged.object")}</TableHead>
                              <TableHead className="text-right">{t("operatorObligationsPanel.merged.interval")}</TableHead>
                              <TableHead className="text-right">{t("operatorObligationsPanel.merged.assets")}</TableHead>
                              <TableHead>{t("operatorObligationsPanel.merged.duties")}</TableHead>
                              <TableHead className="text-right">{t("operatorObligationsPanel.merged.saved")}</TableHead>
                              <TableHead />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.appointments.merged.map((row: any) => (
                              <TableRow key={`${row.regulatedObjectCode}|${row.intervalMonths}|${row.obligationKeys.join(",")}`}>
                                <TableCell className="break-words">{regulatedObjectName(t, row.regulatedObjectCode)}</TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {t("operatorObligationsPanel.merged.everyMonths", { count: row.intervalMonths })}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{row.assets.toLocaleString("de-DE")}</TableCell>
                                <TableCell className="min-w-[280px]">
                                  <ul className="space-y-0.5 text-xs">
                                    {row.obligations.map((o: any) => (
                                      <li key={o.obligationKey} className="break-words" title={o.description}>
                                        <span className="font-medium">{[o.regulation, o.provisionId].filter(Boolean).join(" ")}</span>
                                        {o.description ? ` — ${o.description.slice(0, 90)}${o.description.length > 90 ? "…" : ""}` : ""}
                                      </li>
                                    ))}
                                  </ul>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{row.checklistsPerYearSaved.toLocaleString("de-DE")}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t("operatorObligationsPanel.merged.showChecklist")}
                                    title={t("operatorObligationsPanel.merged.showChecklist")}
                                    onClick={() => setMergedPreview(row)}
                                  >
                                    <Eye aria-hidden className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : null}
                  </Section>
                ) : null}
            </ScrollPane>
          </TabsContent>
        </Tabs>
          <MergedChecklistPreviewDialog
            appointment={mergedPreview}
            bindingId={selected !== ALL ? selected : undefined}
            onClose={() => setMergedPreview(null)}
          />

        </>
      )}
    </div>
  );
}
